import api from './api';
import products from 'api/shopify/data/products';
import { reactive } from '@resn/gozer-core/reactivity';
import { nanoid } from 'nanoid';
import PRODUCT_TYPES from 'api/shopify/data/product-types';
import { mapShopifyItem } from './map-shopify-item';
import { appendUTMToUrl, convertToMetafieldInput, parseUTMTags } from './parse-utm-tags';
import { EventEmitter } from 'eventemitter3';
import { prefixShopifyUrl } from './checkout-url';

/**
 * Hardcode a version number for local storage in case
 * we want to clear local storage between deploys.
 */
const CART_LS_VERSION = '1.2';

class ShopifyService extends EventEmitter {
    constructor() {
        super();
        this.api = api;
        this.products = products;
        this.inited = false;

        this.configure = {
            enabled: false,
            type: null, // xs1 | xp1
            lineIds: [],
            packageId: null,
            subscriptionId: null,
            colorId: null,
            barId: null,
            groupId: null,
        };

        this.cart = {
            subtotal: 0,
            lines: [],
        };

        this.props = reactive({
            count: 0,
            products: [],
        });

        this.groups = [];
        this.utmTags = parseUTMTags();
    }

    async _init() {
        await this._initProducts();
        await this._initCart();
    }

    clearCartStorage() {
        window.localStorage.setItem('cartId', null);
        window.localStorage.setItem('cartGroups', null);
    }

    async _initCart() {
        // No more Cart...
        this.clearCartStorage();

        const lsVersion = window.localStorage.getItem('cartVersion');
        if (!lsVersion || lsVersion !== CART_LS_VERSION) {
            this.clearCartStorage();
        }
        window.localStorage.setItem('cartVersion', CART_LS_VERSION);

        this.cartId = window.localStorage.getItem('cartId');
        let groupsParsed = false;
        try {
            this.groups = JSON.parse(window.localStorage.getItem('cartGroups'));
            if (this.groups) {
                groupsParsed = true;
            }
        } catch (err) {
            groupsParsed = false;
        }

        if (!groupsParsed || !this.cartId) {
            this.cartId = null;
            this.groups = [];
            this.clearCartStorage();
        }

        // Maybe have existing cart, try to get it
        if (this.cartId) {
            this.cart = await api.cart.getCart({
                id: this.cartId,
            });
            if (this.cart) {
                this.updateCount();
            } else {
                this.cartId = null;
            }
        }

        // Create new cart
        if (!this.cartId) {
            this.cartId = await api.cart.createCart();
            window.localStorage.setItem('cartId', this.cartId);

            this.cart = {
                lines: [],
                subtotal: 0,
            };
        }
    }

    async _initProducts() {
        let results = await api.product.getProducts();

        this.products = this.products
            .map((product) => {
                let variantNode = this.getVariantByUiId(results.edges, product.id);

                if (!variantNode) {
                    console.warn(
                        `${product.id} does not exist in Shopify. If this is a new environment, ensure to set Storefront Metafield Visibility.`,
                    );
                    return null;
                } else {
                    return mapShopifyItem(product, variantNode);
                }
            })
            .filter((item) => item !== null);

        this.props.products = this.products;
        this.inited = true;

        this.emit('inited', {
            products: this.products,
        });
    }

    /**
     * Centralised monthly price calculator for
     */
    calculateCartMonthly(total) {
        const monthly = total / 48;
        if (isNaN(monthly)) {
            console.warn('The monthly cart price was set incorrectly. Supplied value:', monthly);
            return 'Error';
        } else {
            return monthly;
        }
    }

    updateCount() {
        let count = 0;
        for (let line of this.cart.lines) {
            count += line.node.quantity;
        }
        this.props.count = count;
    }

    getUiIdByMetafields(metafields) {
        let edge = metafields.edges.find((edge) => {
            if (!edge.node) return null;
            return edge.node.key == 'ui_id';
        });

        if (!edge) return null;
        return edge.node.value;
    }

    getVariantByUiId(productEdges, uiid) {
        for (let productEdge of productEdges) {
            // console.log('productEdge', productEdge);
            let productNode = productEdge.node;
            // console.log('productNode', productNode);

            // Product has only variant
            if (uiid == this.getUiIdByMetafields(productNode.metafields)) {
                if (productNode.variants) {
                    return productNode.variants.edges[0].node;
                }
            }

            // Product has multiple variants
            else {
                for (let variantEdge of productNode.variants.edges) {
                    // console.log('variantEdge', variantEdge);
                    let variantNode = variantEdge.node;
                    // console.log('variantNode', variantNode);
                    if (uiid == this.getUiIdByMetafields(variantNode.metafields)) {
                        return variantNode;
                    }
                }
            }
        }
    }

    getProductsByType(type) {
        let products = this.products.filter((product) => product.type == type);
        if (!products.length) {
            console.warn(`There are no products with type: ${type}`);
        }

        return products;
    }

    getProductByUiid(uiid) {
        let product = this.products.find((product) => product.id == uiid);
        if (!product) {
            console.warn(`There is no product with uiid: ${uiid}`);
        }
        return product;
    }

    getProductByVariantId(variantId) {
        let product = this.products.find((product) => product.variantId == variantId);
        if (!product) {
            console.warn('No product with variant id', variantId);
            return;
        }
        return product;
    }

    getLineByVariantId(variantId) {
        return this.cart.lines.find((line) => {
            return line.node.merchandise.id == variantId;
        });
    }

    async incrementMerch(variantId) {
        this.cart = await api.cart.addToCart({
            cartId: this.cartId,
            lines: [
                {
                    merchandiseId: variantId,
                },
            ],
        });

        this.updateCount();

        return this.cart;
    }

    async decrementMerch(variantId) {
        let line = this.getLineByVariantId(variantId);
        if (!line) return this.cart;

        if (line.node.quantity > 1) {
            this.cart = await api.cart.updateCart({
                cartId: this.cartId,
                lines: [
                    {
                        id: line.node.id,
                        quantity: line.node.quantity - 1,
                    },
                ],
            });
        } else {
            this.cart = await api.cart.removeFromCart({
                cartId: this.cartId,
                lineIds: [line.node.id],
            });
        }

        this.updateCount();

        return this.cart;
    }

    // Jamie: Unused.
    // async addMerch(variantId, quantity) {
    //     this.cart = await api.cart.addToCart({
    //         cartId: this.cartId,
    //         lines: [
    //             {
    //                 merchandiseId: variantId,
    //                 quantity,
    //             },
    //         ],
    //     });

    //     this.updateCount();

    //     return this.cart;
    // }

    async addMerches(lines) {
        console.log('ShopifyService.addMerches', lines);
        this.cart = await api.cart.addToCart({
            cartId: this.cartId,
            lines,
        });

        this.updateCount();

        return this.cart;
    }

    getXS1Groups() {
        const groups = this.groups;
        const xs1Data = groups
            .reduce((acc, group) => {
                let subscription;
                let product; // package
                let { groupId } = group;
                group.lines.forEach((line) => {
                    const p = this.getProductByVariantId(line.merchandiseId);
                    if (p.type === PRODUCT_TYPES.XS1_SUBSCRIPTION) {
                        subscription = p;
                    } else if (p.type === PRODUCT_TYPES.XS1) {
                        product = p;
                    }
                });
                acc.push({
                    subscription,
                    product,
                    groupId,
                });
                return acc;
            }, [])
            .filter((entry) => {
                return entry.subscription && entry.product;
            });

        return xs1Data;
    }

    getXP1Groups() {
        const groups = this.groups;
        const xp1Data = groups
            .reduce((acc, group) => {
                let subscription;
                let product; // package;
                let bar;
                let { groupId } = group;

                group.lines.forEach((line) => {
                    const p = this.getProductByVariantId(line.merchandiseId);
                    if (p.type === PRODUCT_TYPES.XP1_SUBSCRIPTION) {
                        subscription = p;
                    } else if (p.type === PRODUCT_TYPES.XP1) {
                        product = p;
                    } else if (p.type === PRODUCT_TYPES.XP1_BAR) {
                        bar = p;
                    }
                });
                acc.push({
                    subscription,
                    product,
                    bar,
                    groupId,
                });
                return acc;
            }, [])
            .filter((entry) => {
                return entry.subscription && entry.product && entry.bar;
            });

        return xp1Data;
    }

    async addMerchesGroup(lines) {
        await this.addMerches(lines);
        this.groups.push({
            lines,
            groupId: nanoid(),
        });
        window.localStorage.setItem('cartGroups', JSON.stringify(this.groups));
        console.log('ShopifyService.addMerchesGroup', this.groups);
    }

    async removeMerchesGroup(groupId) {
        let g = this.groups;
        this.groups = this.groups.filter((g) => {
            return g.groupId !== groupId;
        });
        console.log('REMOVE MERCH GROUP', groupId, g.length, this.groups.length);
        window.localStorage.setItem('cartGroups', JSON.stringify(this.groups));
    }

    async removeMerch(variantId) {
        let line = await this.getLineByVariantId(variantId);
        if (!line) return this.cart;

        this.cart = await api.cart.removeFromCart({
            cartId: this.cartId,
            lineIds: [line.node.id],
        });

        this.updateCount();

        return this.cart;
    }

    setConfigureMode(data) {
        Object.assign(this.configure, data);
    }

    clearConfigureMode() {
        this.configure = {
            editting: false,
            type: null, // xs1 | xp1
            packageId: null,
            colorId: null,
            subscriptionId: null,
            barId: null,
            groupId: null,
        };
    }

    isCartEmpty() {
        return this.cart.lines && this.cart.lines.length === 0;
    }

    // Jamie: Unused.
    // async decrementMerchesInEdit() {
    //     if (!this.configure.editting) return;
    //     switch (this.configure.type) {
    //         case 'xs1': {
    //             let product = this.getProductByUiid(`xs1-${this.configure.packageId}-${this.configure.colorId}`);
    //             await this.decrementMerch(product.variantId);

    //             let sub = this.getProductByUiid(`xs1-sub-${this.configure.packageId}-yearly`);
    //             await this.decrementMerch(sub.variantId);

    //             break;
    //         }
    //         case 'xp1': {
    //             let product = this.getProductByUiid(`xp1-downpayment`);
    //             await this.decrementMerch(product.variantId);

    //             let sub = this.getProductByUiid(`xp1-sub-yearly`);
    //             await this.decrementMerch(sub.variantId);

    //             let bar = this.getProductByUiid(`xp1-bar-${this.configure.barId}`);
    //             await this.decrementMerch(bar.variantId);

    //             break;
    //         }
    //     }
    // }

    async toCheckout() {
        console.log('ShopifyService.toCheckout');

        // const userId = window.localStorage.getItem('userId');
        // const email = window.localStorage.getItem('email');

        const input = {
            lineItems: this.cart.lines.map((line) => {
                return {
                    variantId: line.node.merchandise.id,
                    quantity: line.node.quantity,
                };
            }),
            customAttributes: [],
        };

        // if (userId) {
        //     input.customAttributes = [
        //         {
        //             key: 'userId',
        //             value: userId,
        //         },
        //     ];
        // }

        if (this.utmTags) {
            input.customAttributes = [...input.customAttributes, ...convertToMetafieldInput(this.utmTags)];
        }

        // if (email) {
        //     input.email = email;
        // }

        /**
         * Clear the cart
         */
        this.cartId = null;
        window.localStorage.setItem('cartId', null);
        window.localStorage.setItem('cartGroups', null);

        let { url } = await api.checkout.createCheckout({ input });
        if (!url) return;

        if (this.utmTags) {
            url = appendUTMToUrl(url, this.utmTags);
        }
        console.log(url);

        // Error on mobile causing redirect to root /
        // Any other method is causing state to be maintained of current page ( i.e. Directing to Payment screen is shown continuously )
        // window.location.replace(url);
        window.location.assign(url);
    }

    async toCheckoutWithLines(lines, customAttributes = []) {
        console.log('ShopifyService.toCheckoutWithLines', lines);

        const input = {
            lineItems: lines.map((line) => {
                return {
                    variantId: line.merchandiseId,
                    quantity: line.quantity || 1,
                };
            }),
            customAttributes: customAttributes ? customAttributes : [],
        };

        if (this.utmTags) {
            input.customAttributes = [...input.customAttributes, ...convertToMetafieldInput(this.utmTags)];
        }
        /**
         * Clear the cart
         */
        // this.cartId = null;
        // window.localStorage.setItem('cartId', null);
        // window.localStorage.setItem('cartGroups', null);

        console.log('Custom Attribute: ', input.customAttributes);
        let { url } = await api.checkout.createCheckout({ input });
        if (!url) return;

        if (this.utmTags) {
            url = appendUTMToUrl(url, this.utmTags);
        }

        /** Prefix to the correct domain Shopify checkout URL */
        url = prefixShopifyUrl(url);
        // Error on mobile causing redirect to root /
        // Any other method is causing state to be maintained of current page ( i.e. Directing to Payment screen is shown continuously )
        // window.location.replace(url);
        window.location.assign(url);
    }
}

const service = new ShopifyService();
window.ShopifyService = service;
export default service;
